<template>
  <div class="cv-send-history-modal">
    <!-- Loading -->
    <div v-if="is_loading" class="loading-container">
      <i class="fas fa-spinner fa-spin"></i>
      <span>טוען היסטוריית שליחות...</span>
    </div>

    <div v-else-if="load_error" class="notification is-danger is-light">
      שגיאה בטעינת היסטוריית השליחות
    </div>

    <div v-else>
      <!-- Sends -->
      <h3 class="section-title">שליחות</h3>

      <div v-if="allSends.length === 0" class="empty-state">
        <p>אין תיעוד שליחות להצעה זו</p>
        <p v-if="history.sent_at" class="empty-note">
          תאריך שליחה רשום: {{ formatDate(history.sent_at) }}
        </p>
      </div>

      <table v-else class="table is-fullwidth is-striped sends-table">
        <thead>
          <tr>
            <th>תאריך</th>
            <th>ערוץ</th>
            <th>מצב שליחה</th>
            <th>קבצים</th>
            <th>נמסר</th>
            <th>נשלח ע"י</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(row, index) in allSends"
            :key="index"
            :class="{ 'partial-row': row.partial }"
            :title="row.partial ? partialHint : ''"
          >
            <td>{{ formatDate(row.sent_at) }}</td>
            <td>{{ channelLabel(row.channel) }}</td>
            <td>{{ modeLabel(row.mode) }}</td>
            <td>
              <span v-if="!row.files || row.files.length === 0">—</span>
              <template v-else>
                <div v-for="(file, f_index) in row.files" :key="f_index" class="file-name">
                  {{ file }}
                </div>
              </template>
            </td>
            <td>{{ row.partial || !row.delivered_at ? '—' : formatDate(row.delivered_at) }}</td>
            <td>{{ row.owner || '—' }}</td>
          </tr>
        </tbody>
      </table>

      <!-- Exposure -->
      <h3 class="section-title">חשיפה</h3>

      <ul v-if="exposures.length > 0" class="exposure-list">
        <li v-for="(exposure, index) in exposures" :key="index">
          {{ formatDate(exposure.exposed_at) }} - {{ exposure.content || '—' }}
        </li>
      </ul>
      <p v-else-if="history.last_exposed" class="exposure-fallback">
        המועמד/ת נחשפ/ה לחברה זו (רשומה כללית): {{ formatDate(history.last_exposed) }}
      </p>
      <p v-else class="exposure-none">לא נחשף</p>
    </div>
  </div>
</template>

<script>
import NetworkUtils from '@/custom/classes/network_utils.js'

const CHANNEL_LABELS = {
  email: 'אימייל',
  lite_email: 'אימייל לייט',
  whatsapp: 'וואטסאפ'
}

const MODE_LABELS = {
  normal: 'רגיל (חשוף)',
  lite: 'לייט (דיסקרטי)',
  super_lite: 'סופר לייט (דיסקרטי)'
}

export default {
  name: 'CvSendHistoryModal',
  props: {
    payload: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      networkUtils: new NetworkUtils(),
      is_loading: true,
      load_error: false,
      partialHint: 'תיעוד חלקי - שליחה מלפני יולי 2026',
      history: {
        sent_at: null,
        sends: [],
        legacy_sends: [],
        exposures: [],
        last_exposed: null
      }
    }
  },
  computed: {
    offer () {
      return (this.payload.props && this.payload.props.offer) || {}
    },
    exposures () {
      return this.history.exposures || []
    },
    // Modern records and legacy stats are one timeline for the recruiter; the
    // `partial` flag is what tells her which rows are missing detail.
    allSends () {
      const sends = this.history.sends || []
      const legacy = this.history.legacy_sends || []
      return sends.concat(legacy).slice().sort((a, b) => {
        const a_time = a.sent_at ? new Date(a.sent_at).getTime() : 0
        const b_time = b.sent_at ? new Date(b.sent_at).getTime() : 0
        return b_time - a_time
      })
    }
  },
  created () {
    this.loadHistory()
  },
  methods: {
    async loadHistory () {
      if (!this.offer || !this.offer.id) {
        this.is_loading = false
        this.load_error = true
        return
      }
      try {
        const response = await this.networkUtils.getCvSendHistory({ offer_id: this.offer.id })
        this.history = {
          sent_at: response.sent_at || null,
          sends: response.sends || [],
          legacy_sends: response.legacy_sends || [],
          exposures: response.exposures || [],
          last_exposed: response.last_exposed || null
        }
      } catch (error) {
        console.error('Error loading CV send history:', error)
        this.load_error = true
      } finally {
        this.is_loading = false
      }
    },
    formatDate (value) {
      if (!value) return '—'
      return this.$moment(value).format('DD/MM/YYYY HH:mm')
    },
    channelLabel (channel) {
      return CHANNEL_LABELS[channel] || '—'
    },
    modeLabel (mode) {
      return MODE_LABELS[mode] || 'לא ידוע'
    }
  }
}
</script>

<style scoped>
.cv-send-history-modal {
  direction: rtl;
  text-align: right;
  padding: 16px;
}

.loading-container {
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: center;
  padding: 32px 0;
  color: #555;
}

.section-title {
  font-size: 1.1rem;
  font-weight: 600;
  margin: 16px 0 8px;
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 4px;
}

.sends-table th,
.sends-table td {
  text-align: right;
  vertical-align: middle;
  font-size: 0.9rem;
}

.partial-row {
  color: #9a9a9a;
  font-style: italic;
}

.file-name {
  white-space: nowrap;
}

.empty-state {
  padding: 12px;
  background: #f7f7f7;
  border-radius: 4px;
  color: #555;
}

.empty-note {
  margin-top: 4px;
  font-size: 0.85rem;
}

.exposure-list {
  list-style: disc;
  padding-right: 20px;
}

.exposure-fallback,
.exposure-none {
  color: #555;
}
</style>
